import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeesTableSchema, FilterType } from '../../types/types';
import { getFeeGroupsThunk, getFeesThunk } from './thunks';

interface TableState {
    loading: boolean;
    error: boolean;
    data: FeesTableSchema[];
    feeGroups: FilterType[] | null;
    filters: {
        feeGroup: number | null;
        description: string | null;
        validUntil: Date | null;
        currency: string;
    }
    pagination: {
        totalRecords: number;
        first: number;
        rowsPerPage: number;
        page: number;
    }
    searchIsActive: boolean;
}

const initialState: TableState = {
    loading: false,
    error: false,
    data: [],
    feeGroups: [],
    filters: {
        feeGroup: null,
        description: null,
        validUntil: null,
        currency: "EUR",
    },
    pagination: {
        totalRecords: 0,
        first: 0,
        rowsPerPage: 10,
        page: 0,
    },
    searchIsActive: false,
};

const tableSlice = createSlice({
    name: 'table',
    initialState,
    reducers: {
        setFeeGroup: (state, action: PayloadAction<number>) => {
            state.filters.feeGroup = action.payload;
            state.searchIsActive = true;
        },
        setDescription: (state, action: PayloadAction<string | null>) => {
            state.filters.description = action.payload;
            state.searchIsActive = true;
        },
        setValidUntil: (state, action: PayloadAction<Date | null>) => {
            state.filters.validUntil = action.payload;
            state.searchIsActive = true;
        },
        setCurrency: (state, action: PayloadAction<string>) => {
            state.filters.currency = action.payload;
            state.searchIsActive = true;
        },
        updatePagination: (state, action: PayloadAction<{ first: number, rows?: number, page: number }>) => {
            state.pagination.first = action.payload.first;
            action.payload.rows && (state.pagination.rowsPerPage = action.payload.rows);
            state.pagination.page = action.payload.page;
        },
        updatePaginationFirst: (state, action: PayloadAction<number>) => {
            state.pagination.first = action.payload;
        },
        clearFiltersAndPagination: (state) => {
            state.pagination = initialState.pagination;
            state.filters = initialState.filters;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getFeesThunk.pending, (state) => {
            state.loading = true;
            state.error = false;
            state.searchIsActive = false;
        }).addCase(getFeesThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
            state.pagination.totalRecords = action.payload.length;
            state.searchIsActive = false;
        }).addCase(getFeesThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.searchIsActive = true;
        }).addCase(getFeeGroupsThunk.pending, (state) => {
            state.loading = true;
            state.error = false;
        }).addCase(getFeeGroupsThunk.fulfilled, (state, action: PayloadAction<FilterType[]>) => {
            state.loading = false;
            state.feeGroups = action.payload;
        }).addCase(getFeeGroupsThunk.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    }
});

export default tableSlice;